import React, { useState, useEffect } from "react";
import Picker from "react-mobile-picker";
import Lottie from "lottie-react";
import { motion, AnimatePresence } from "framer-motion";

import Button from "../../home/components/Button";
import { ReactComponent as DDxEdit } from "../../../assets/images/ddx-edit.svg";
import * as HandScroll from "../../../assets/animations/hand_scroll.json";

interface PatientAgeInputProps {
  age: number | null;
  /** Called with the picked value; never with an updater. */
  setAge: (age: number | null) => void;
  disabled?: boolean;
  onPressEdit: () => void;
  onPressSend?: (prompt?: any, age?: number) => void;
}

const PatientAgeInput: React.FC<PatientAgeInputProps> = ({
  age,
  setAge,
  disabled = false,
  onPressEdit,
  onPressSend = (prompt?: any, age?: number) => {},
}) => {
  const [pickerValue, setPickerValue] = useState({
    age: age?.toString() ?? "18",
  });

  const [tempValue, setTempValue] = useState<any>(null);

  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  useEffect(() => {
    if (age !== null) return;

    const handleTouchMove = (e: TouchEvent) => {
      // Check if the touch target is within the picker
      const target = e.target as HTMLElement;
      const isPickerColumn = target.closest(".react-mobile-picker-column");

      if (!isPickerColumn) {
        // If not touching the picker column, prevent the scroll
        e.preventDefault();
      }
    };

    // Add listener to the document with passive: false to allow preventDefault
    document.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      document.removeEventListener("touchmove", handleTouchMove);
    };
  }, [age]);

  const renderState = () => {
    switch (age !== null) {
      case true:
        return (
          <motion.div
            key={`answered-${age}`}
            initial={{
              opacity: 0,
              scale: 0.5,
              transformOrigin: "top right",
            }}
            animate={{ opacity: 1, scale: 1, transformOrigin: "top right" }}
            exit={{ opacity: 0, scale: 0.5, transformOrigin: "top right" }}
            transition={{ duration: 0.2 }}
            className="flex flex-row items-end gap-2.5 my-7 justify-end"
          >
            <button
              onClick={() => {
                onPressEdit();
                setTempValue(age);
                setIsEditMode(true);
                setAge(null);
                setPickerValue({ age: "18" });
              }}
              className="mb-3"
            >
              <DDxEdit className="min-h-6 min-w-6" />
            </button>
            <div className="bg-white rounded-l-[18px] rounded-tr-[18px] shadow-[0px_0px_12px_0px_#A238FF0D] p-3.5 flex flex-col gap-4">
              <p className="text-sm leading-5 text-gray-800 font-normal">
                <span className="font-semibold">Patient&apos;s Age: </span>
                {age} Years
              </p>
            </div>
          </motion.div>
        );
      case false:
        return (
          <motion.div
            key={"unanswered"}
            initial={{
              opacity: 0,
              scale: 0.5,
              transformOrigin: "top right",
            }}
            animate={{ opacity: 1, scale: 1, transformOrigin: "top right" }}
            exit={{ opacity: 0, scale: 0.5, transformOrigin: "top right" }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-l-[18px] rounded-tr-[18px] ml-7 my-7 shadow-[0px_0px_12px_0px_#A238FF0D] p-3.5 flex flex-col gap-4"
          >
            <p className="font-semibold text-gray-800 text-base leading-6">
              Select Patient&apos;s Age
            </p>
            <div className="relative">
              <Picker
                value={pickerValue}
                onChange={setPickerValue}
                wheelMode="natural"
              >
                <Picker.Column key={"age"} name={"age"}>
                  {Array.from({ length: 99 }, (_, i) => (i + 2).toString()).map(
                    (option) => {
                      const isSelected: boolean = option === pickerValue.age;
                      return (
                        <Picker.Item
                          key={option}
                          value={option}
                          className={
                            isSelected
                              ? "px-8 py-2 bg-[#4B4AD51A] rounded-xl border-2 border-[#4B4AD5] w-fit mx-auto font-bold text-lg"
                              : ""
                          }
                        >
                          {option}
                        </Picker.Item>
                      );
                    }
                  )}
                </Picker.Column>
              </Picker>
              <Lottie
                animationData={HandScroll}
                autoPlay
                autoplay
                loop
                className="absolute z-10 top-1/2 left-1/2 h-11 w-11 pointer-events-none"
              />
            </div>
            <Button
              label="Select Age"
              onClick={() => {
                setAge(parseInt(pickerValue.age));
                if (isEditMode && tempValue !== parseInt(pickerValue.age)) {
                  onPressSend(null, parseInt(pickerValue.age));
                  setIsEditMode(false);
                  setTempValue(null);
                }
              }}
              className="text-sm mt-0"
            />
          </motion.div>
        );
      default:
        return <></>;
    }
  };

  return (
    <div className={`${disabled && "opacity-50 pointer-events-none"}`}>
      <AnimatePresence mode="wait">{renderState()}</AnimatePresence>
    </div>
  );
};

export default PatientAgeInput;
